import { LayoutSizeContext } from 'App';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherWidgetDetails from 'features/Weather/components/WeatherWidgetDetails/WeatherWidgetDetails';
import WeatherWidgetSelector from 'features/Weather/components/WeatherWidgetSelector/WeatherWidgetSelector';
import useLocation from 'hooks/useLocation';
import { useContext, useMemo, useState } from 'react';
import { PiWaves } from 'react-icons/pi';
import { useGetTideTableQuery } from 'services/weather/marine/marine.api';
import { joinClasses } from 'utils/componentStyles/style.utils';
import MarineWidgetSelect from '../../components/MarineWidgetSelect/MarineWidgetSelect';
import {
  getTideForecastCardKey,
  getTideTableLocations,
} from '../../features/TideTable/utils/tideTable.utils';
import {
  getActiveMarineWidgetItem,
  getMarineWidgetActiveItemIndex,
  getMarineWidgetErrorMessage,
} from '../../utils/marineForecastWidget.utils';
import './assets/tideTableWidget.scss';
import { details, layoutConfig, route } from './config/tideTableWidget.config';
import {
  getTideTableWidgetForecastDateLabel,
  getTideTableWidgetForecastDayLabel,
  getTideTableWidgetLayout,
  getTideTableWidgetLocationOptions,
  getTideTableWidgetLocations,
  getTideTableWidgetTileAriaLabel,
} from './utils/tideTableWidget.utils';

const TideTableWidget = ({ layout = '1x1', dimensions, containerSizePx }) => {
  const layoutSize = useContext(LayoutSizeContext);
  const { locations } = useLocation();
  const {
    data: tideEntries = [],
    isLoading: isLoadingTideTable,
    isFetching: isFetchingTideTable,
    isError: isTideTableError,
    error: tideTableError,
    refetch: refetchTideTable,
    fulfilledTimeStamp,
  } = useGetTideTableQuery(locations.effectiveLocation);
  const [selectedLocationKey, setSelectedLocationKey] = useState(null);
  const [selectedForecastIndex, setSelectedForecastIndex] = useState(0);

  const tideLocations = useMemo(
    () => getTideTableWidgetLocations(getTideTableLocations(tideEntries)),
    [tideEntries],
  );
  const locationOptions = useMemo(
    () => getTideTableWidgetLocationOptions(tideLocations),
    [tideLocations],
  );
  const activeLocation = getActiveMarineWidgetItem(
    tideLocations,
    selectedLocationKey,
    ({ locationKey }) => locationKey,
  );
  const forecasts = activeLocation?.forecasts ?? [];
  const activeForecastIndex = getMarineWidgetActiveItemIndex(forecasts, selectedForecastIndex);
  const selectedForecast = forecasts[activeForecastIndex];
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];
  const widgetLayout = getTideTableWidgetLayout({
    containerSizePx,
    layoutSize,
    dimensions,
  });
  const isRefreshing = isFetchingTideTable && !isLoadingTideTable;

  if (isTideTableError && !isLoadingTideTable) {
    return (
      <ErrorMessage
        message={getMarineWidgetErrorMessage(tideTableError, 'Unable to load tide table.')}
        retryRoute={route}
        onRetry={() => refetchTideTable()}
      />
    );
  }

  if (isLoadingTideTable) {
    return <WidgetLoadingAnimation widgetName="TideTable" dimensions={dimensions} />;
  }

  if (!tideLocations.length || !forecasts.length) {
    return (
      <section aria-label="Tide table widget">
        <WidgetTimeUpdate
          route={route}
          compact={config.compactTimeUpdate}
          updatedAt={fulfilledTimeStamp ?? null}
        />
        <p className="p-3 mb-0" role="status">
          No tide forecast data found.
        </p>
      </section>
    );
  }

  return (
    <section
      aria-label="Tide table widget"
      aria-busy={isRefreshing || undefined}
      className={joinClasses('tide-table-widget', config.wrapperClassName)}
      data-layout={layout}
    >
      <WidgetTimeUpdate
        route={route}
        compact={config.compactTimeUpdate}
        updatedAt={fulfilledTimeStamp ?? null}
      />
      {isRefreshing && (
        <span className="visually-hidden" role="status">
          Updating tide table.
        </span>
      )}

      <div className={config.contentClassName}>
        <div className={config.controlsClassName}>
          <MarineWidgetSelect
            label="Tide location"
            options={locationOptions}
            selectedValue={activeLocation?.locationKey}
            placeholder="Select a tide location"
            isSearchable
            onSelect={(locationKey) => {
              setSelectedLocationKey(locationKey ?? null);
              setSelectedForecastIndex(0);
            }}
          />
        </div>

        <div
          className="pt-2"
          style={{
            flex: `0 0 ${config.selectorHeight}`,
            height: config.selectorHeight,
            minHeight: 0,
          }}
        >
          <WeatherWidgetSelector
            items={forecasts}
            selectedIndex={activeForecastIndex}
            visibleNumOfItems={config.visibleCount(widgetLayout)}
            selectorMode={config.selectorMode}
            gridColumnCount={config.gridColumnCount}
            gap={config.selectorGap}
            arrowButtonWidth={widgetLayout.arrowButtonWidth}
            arrowIconSize={widgetLayout.arrowIconSize}
            listContainerStyle={config.selectorListStyle}
            tileStyle={config.selectorTileStyle}
            ariaLabel="Tide forecast days"
            previousButtonLabel="Show previous tide forecast day"
            nextButtonLabel="Show next tide forecast day"
            onSelect={setSelectedForecastIndex}
            getKey={getTideForecastCardKey}
            getTileProps={(forecast) => ({
              label: getTideTableWidgetForecastDayLabel(forecast),
              value: getTideTableWidgetForecastDateLabel(forecast),
              ariaLabel: getTideTableWidgetTileAriaLabel(forecast),
              icon: (
                <span
                  aria-hidden="true"
                  className="tide-table-widget-tile-icon d-flex h-100 w-100 align-items-center justify-content-center"
                >
                  <PiWaves />
                </span>
              ),
              iconStyle: {
                width:
                  config.selectorMode === 'row'
                    ? widgetLayout.rowTileIconSize
                    : widgetLayout.tileIconSize,
                height:
                  config.selectorMode === 'row'
                    ? widgetLayout.rowTileIconSize
                    : widgetLayout.tileIconSize,
              },
              labelStyle: widgetLayout.tileTextStyle,
              valueStyle: widgetLayout.tileTextStyle,
            })}
          />
        </div>

        <WeatherWidgetDetails
          selectedItem={selectedForecast}
          details={details}
          layoutGroups={config.detailGroups}
          containerClassName={config.detailsClassName}
          containerStyle={config.detailsStyle({ layoutSize })}
          iconSize={config.detailsIconSize}
          ariaLabel={`Tide forecast for ${activeLocation?.locationName ?? 'selected location'}, ${getTideTableWidgetForecastDateLabel(
            selectedForecast,
          )}`}
        />
      </div>
    </section>
  );
};

export default TideTableWidget;
